import { createContext, useContext, useState, ReactNode, useCallback } from 'react'
import { api } from '../api/client'
import { toast } from 'sonner'
import { getStoredWbToken, setStoredWbToken, clearStoredWbToken } from '../utils/wbToken'

interface WbTokenContextType {
  token: string | null
  isConnected: boolean
  connecting: boolean
  connect: (token: string) => Promise<boolean>
  disconnect: () => void
}

const WbTokenContext = createContext<WbTokenContextType | undefined>(undefined)

export const useWbToken = () => {
  const context = useContext(WbTokenContext)
  if (!context) {
    throw new Error('useWbToken must be used within WbTokenProvider')
  }
  return context
}

interface WbTokenProviderProps {
  children: ReactNode
}

export const WbTokenProvider = ({ children }: WbTokenProviderProps) => {
  const [token, setToken] = useState<string | null>(() => getStoredWbToken())
  const [connecting, setConnecting] = useState(false)

  const connect = useCallback(async (newToken: string): Promise<boolean> => {
    setConnecting(true)
    try {
      await api.post('/wb/connect', {}, { headers: { 'X-WB-Token': newToken } })
      setStoredWbToken(newToken)
      setToken(newToken)
      return true
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Не удалось подключить токен')
      return false
    } finally {
      setConnecting(false)
    }
  }, [])

  const disconnect = useCallback(() => {
    clearStoredWbToken()
    setToken(null)
    toast.info('Токен отключён')
  }, [])

  return (
    <WbTokenContext.Provider value={{ token, isConnected: !!token, connecting, connect, disconnect }}>
      {children}
    </WbTokenContext.Provider>
  )
}
